import { useEffect, useMemo, useState } from 'react';

/**
 * True while the promo hero should show a neutral placeholder instead of the
 * default athlete photo — i.e. a deal image is expected/known but its bytes
 * haven't painted yet. This prevents the default background from flashing
 * behind a still-downloading deal image.
 *
 * @param imageUrl   the resolved deal image URL ('' until resolved)
 * @param imageLoaded whether the deal <img> has fired onLoad
 *
 * Bounded by a timeout so it falls back to the default hero if no deal image
 * ever loads (e.g. a stray match id with no deal).
 */
export function useAwaitingDeal(imageUrl: string, imageLoaded: boolean, timeoutMs = 10000): boolean {
  // A deal is expected if the URL carries a Branch match id / deal param,
  // even before it resolves.
  const expectedFromUrl = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return p.has('_branch_match_id') || p.has('deal');
  }, []);

  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!expectedFromUrl && !imageUrl) return;
    const t = setTimeout(() => setExpired(true), timeoutMs);
    return () => clearTimeout(t);
  }, [expectedFromUrl, imageUrl, timeoutMs]);

  const dealExpected = expectedFromUrl || !!imageUrl;
  return dealExpected && !imageLoaded && !expired;
}

/**
 * True while a deal is expected from the URL (Branch match id / deal param) but
 * hasn't resolved yet — used to show a co-brand skeleton instead of the plain
 * URUNN logo, avoiding a logo/banner swap when the deal arrives.
 */
export function useDealPending(hasDeal: boolean, timeoutMs = 10000): boolean {
  const expectedFromUrl = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return p.has('_branch_match_id') || p.has('deal');
  }, []);

  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!expectedFromUrl) return;
    const t = setTimeout(() => setExpired(true), timeoutMs);
    return () => clearTimeout(t);
  }, [expectedFromUrl, timeoutMs]);

  return expectedFromUrl && !hasDeal && !expired;
}
